/**
 * Google Tag Manager Utility
 * Standardized GTM event pushes aligned with GA4 eCommerce format
 */

export const GTM_ID = process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID;

/**
 * Push data to GTM dataLayer
 */
const pushToDataLayer = (data) => {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(data);
};

/**
 * Page View Event
 * Triggered when a page is viewed
 */
export const pageview = (url) => {
  pushToDataLayer({
    event: "page_view",
    page_location: url,
  });
};

/**
 * View Item Event
 * Triggered when a user views a product detail page
 */
export const viewItemEvent = (item) => {
  pushToDataLayer({
    event: "view_item",
    ecommerce: {
      items: [
        {
          item_id: item.id || item.slug,
          item_name: item.name || "",
          price: item.price || 0,
          currency: "BDT",
          item_brand: item.brand?.name || "",
          item_category: Array.isArray(item.categories)
            ? item.categories.map((c) => c.name).join(", ")
            : "",
        },
      ],
    },
  });
};

/**
 * Sign In Event
 */
export const signInEvent = (method = "mobile") => {
  pushToDataLayer({
    event: "sign_in",
    method,
    status: "success",
  });
};

/**
 * Sign Up Event
 */
export const signUpEvent = (method = "mobile") => {
  pushToDataLayer({
    event: "sign_up",
    method,
    status: "completed",
  });
};

/**
 * Add to Cart Event
 */
export const addToCartEvent = (item, quantity = 1) => {
  pushToDataLayer({
    event: "add_to_cart",
    ecommerce: {
      currency: "BDT",
      value: (item.price || 0) * quantity,
      items: [
        {
          item_id: item.id || item.slug,
          item_name: item.name || "",
          price: item.price || 0,
          quantity,
          item_brand: item.brand?.name || "",
          item_category: Array.isArray(item.categories)
            ? item.categories.map((c) => c.name).join(", ")
            : "",
        },
      ],
    },
  });
};

/**
 * Begin Checkout Event
 */
export const beginCheckoutEvent = (items, total, currency = "BDT") => {
  pushToDataLayer({
    event: "begin_checkout",
    ecommerce: {
      currency,
      value: total,
      items: items.map((item) => ({
        item_id: item.id || item.slug,
        item_name: item.name || "",
        price: item.price || 0,
        quantity: item.quantity || 1,
        item_brand: item.brand?.name || "",
        item_category: Array.isArray(item.categories)
          ? item.categories.map((c) => c.name).join(", ")
          : "",
      })),
    },
  });
};

/**
 * Purchase Event
 */
export const purchaseEvent = (
  transactionId,
  items,
  totalValue,
  currency = "BDT",
  affiliation = "Amarpet Store"
) => {
  pushToDataLayer({
    event: "purchase",
    ecommerce: {
      transaction_id: transactionId,
      affiliation,
      value: totalValue,
      currency,
      items: items.map((item) => ({
        item_id: item.id || item.slug,
        item_name: item.name || "",
        price: item.total || 0,
        quantity: item.quantity || 1,
        item_brand: item.brand?.name || "",
        item_category: Array.isArray(item.categories)
          ? item.categories.map((c) => c.name).join(", ")
          : "",
      })),
    },
  });
};

/**
 * Contact Event (for contact form or customer service)
 */
export const contactEvent = (channel = "form") => {
  pushToDataLayer({
    event: "contact",
    method: channel,
    status: "submitted",
  });
};

/**
 * Search Event
 */
export const searchEvent = (query) => {
  pushToDataLayer({
    event: "search",
    search_term: query,
  });
};
